import * as React from 'react'
import styled, { css } from 'styled-components'
import Icon from '../Icons/Icon'
import { Style } from '../../styles'
import { useTranslation } from 'react-i18next'
import Popover from '../Popover/Popover'
import ActionList from '../ActionList/ActionList'
import { NodeRendererProps } from 'react-arborist'
import { PublicLocale, Locale, HelpCenterTreeData, HelpCenterTreeModelType } from '../../types'
import MobilityHelper from '../../helpers/MobilityHelper'
import IconPopover from './IconPopover'
import SimpleTooltip from '@/components/Tooltips/SimpleTooltip'

const DragButton = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
  cursor: grab;
  opacity: 0;
  transition: opacity 100ms ease-in-out;

  svg {
    width: 10px;
    color: rgb(100, 100, 98);
  }
`

const Collapse = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  transition: transform 100ms ease-in-out;
  margin-left: 20px;
  margin-right: 20px;
  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;
  }
`

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${Style.color.border};
  border-radius: 50%;
  transition: background-color 100ms ease-in-out;
  margin-right: 12px;
  cursor: pointer;

  &:hover {
    background: #e9eae6 !important;
  }

  img {
    width: 26px;
    height: 26px;
  }
`

const Container = styled.div<{ open?: boolean, type: HelpCenterTreeModelType, willReceiveDrop?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  transition: background-color 100ms ease-in-out;
  border-bottom: 1px solid ${Style.color.border};
  background: white;
  width: 100%;
  cursor: grab;

  &:last-child {
    border-bottom: 0;
  }

  
  &:hover {
    background: #f0f1ef;

    ${DragButton} {
      opacity: 1;
    }

    ${IconContainer} {
      background: white;
    }
  }

  ${props => props.willReceiveDrop && css`
    background: #f0f1ef;

    ${DragButton} {
      opacity: 1;
    }
  `}

  ${DragButton} {
    position: absolute;
    left: 8px;
  }

  ${props => !props.open && css`
    ${Collapse} {
      transform: rotate(-90deg);
    }
  `}

  ${props => props.type === HelpCenterTreeModelType.COLLECTION && css`

  `}

  ${props => props.type === HelpCenterTreeModelType.ARTICLE && css`
    ${IconContainer} {
      border-color: transparent;
      background-color: transparent !important;

      &:hover {
        background: transparent !important;
      }
      
      svg {
        width: 26px;
        height: 26px;
      }
    }
  `}

  ${props => props.type === HelpCenterTreeModelType.ADD_ARTICLE && css`
    cursor: pointer;

    ${IconContainer} {
      margin-left: 79px;
      &:hover {
        background: initial !important;
      }
    }
    ${Collapse} {
      display: none;
    }

    ${DragButton} {
      display: none;
    }
  `}
`

const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 16px;
  width: 100%;
`

const Name = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`

const Metadata = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  max-width: 230px;
  cursor: pointer;

  @media screen and (max-width: 1510px) {
    max-width: 170px;
  }

  @media screen and (max-width: 1387px){
    max-width: 110px;
  }
`

const MetadataName = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`

const MetadataDescription = styled.div`
  color: rgb(100, 100, 98);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`

const Status = styled.div`
  display: flex;
  padding: 0px 16px;
  flex: 1;
  justify-content: center;
`

const Articles = styled.div`
  display: flex;
  padding: 0px 16px;
  flex: 1;
  justify-content: center;
`

const Action = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  max-width: 32px;
  padding: 0px 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActionButton = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	text-align: center;
	cursor: pointer;
	font-weight: 600;

	background: ${props => props.theme.buttonBackground};
	color: ${props => props.theme.buttonText};

	i {
		color: ${props => props.theme.buttonText};
	}
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 191.81px;
  margin-right: 10px;
`

type IProps = NodeRendererProps<HelpCenterTreeData> & {
  helpcenterId: string
  locale: PublicLocale
  onClick: (node: HelpCenterTreeData) => void
  onAddArticleClick: (node: HelpCenterTreeData) => void
  onAddCollectionClick: (node: HelpCenterTreeData) => void
  onEditClick: (node: HelpCenterTreeData) => void
  onDeleteClick: (node: HelpCenterTreeData) => void
  onIconClick: (node: HelpCenterTreeData, iconUrl: string) => void
}

const Item = (props: IProps) => {
  const {
    node,
    dragHandle,
    style,
    helpcenterId,
    locale,
    tree,
    onClick,
    onAddArticleClick,
    onAddCollectionClick,
    onEditClick,
    onDeleteClick,
    onIconClick
  } = props
  const { t } = useTranslation()
  const [popoverActive, setPopoverActive] = React.useState(false)

  let type: HelpCenterTreeModelType = node.data.type
  let name = ''
  let description = ''
  let articlesCount = null

  switch (type) {
    case HelpCenterTreeModelType.COLLECTION:
      name = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'title')
      description = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'description')
      articlesCount = node.data.model.articles_count
      break
    case HelpCenterTreeModelType.ARTICLE:
      name = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'title')
      description = MobilityHelper.getTranslation((locale as unknown) as Locale, node.data.model.translations, 'summary')
      break
    case HelpCenterTreeModelType.ADD_ARTICLE:
      description = t('Item::New article')
      break
    default:
      throw new Error(`Invalid type: ${type} given`)
  }


  const onPopoverClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setPopoverActive(!popoverActive)
  }

  const onPopoverClose = () => {
    setPopoverActive(false)
  }

  const onToggleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    node.isOpen ? node.close() : node.open()
  }

  const onItemClick = () => {
    if (type === HelpCenterTreeModelType.ADD_ARTICLE) {
      onAddArticleClick(node.parent.data)
      return
    } else {
      onClick(node.data)
    }
  }

  const onItemAddArticleClick = () => {
    onAddArticleClick(node.data)
  }

  const onItemAddCollectionClick = () => {
    onAddCollectionClick(node.data)
  }

  const onItemEditClick = () => {
    onEditClick(node.data)
  }

  const onItemDeleteClick = () => {
    onDeleteClick(node.data)
  }

  const onIconSubmit = (iconUrl: string) => {
    onIconClick(node.data, iconUrl)
  }

  return (
    <Container
      open={node.isOpen}
      style={{ ...style }}
      type={type}
      willReceiveDrop={node.state.willReceiveDrop}
      onClick={onItemClick}
      ref={dragHandle}
    >
      {type == HelpCenterTreeModelType.ADD_ARTICLE && <DragButton onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}>
        <Icon icon='grip-vertical' />
      </DragButton>}

      <NameContainer>
        <Name>
          {!node.isLeaf && <Collapse onClick={onToggleClick}>
            <Icon icon='chevron-down' />
          </Collapse>}
          {node.isLeaf && <Collapse></Collapse>}

          {type === HelpCenterTreeModelType.COLLECTION && <IconPopover
            helpcenterId={helpcenterId}
            activator={
              <IconContainer>
                <img src={node.data.type === HelpCenterTreeModelType.COLLECTION && node.data.model.icon} />
              </IconContainer>
            }
            onSubmit={onIconSubmit}
          />}
          {type === HelpCenterTreeModelType.ARTICLE && <IconContainer>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><path d="M34.5 32.5v-24a1 1 0 0 0-.29-.71l-7-7A1 1 0 0 0 26.5.5h-20a1 1 0 0 0-1 1v37a1 1 0 0 0 1 1h27a1 1 0 0 0 1-1v-3h-2v2h-21a4 4 0 0 1-4-4v-31h17v7a1 1 0 0 0 1 1h7v22zm-8-29.59l5.59 5.59H26.5z"></path><path d="M11.5 13.5h17v2h-17zm0 5h17v2h-17zm0 5h17v2h-17zm0 5h10v2h-10z"></path></svg>
          </IconContainer>}
          {type === HelpCenterTreeModelType.ADD_ARTICLE && <IconContainer>
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M14 7.15H8.85V2H7.15V7.15H2V8.85H7.15V14H8.85V8.85H14V7.15Z"></path></svg>
          </IconContainer>}


          <Metadata>
            {name?.length > 0 && <MetadataName>
              {name}
            </MetadataName>}

            <MetadataDescription>
              {description}
            </MetadataDescription>
          </Metadata>
        </Name>
      </NameContainer>

      <InfoContainer>

        <Status>
          {type === HelpCenterTreeModelType.COLLECTION && articlesCount === 0 && <SimpleTooltip content={t('Item::A collection need atleast 1 published article to be visible')}>
            <div>
              <Icon icon='exclamation-triangle' />
            </div>
          </SimpleTooltip>}
        </Status>

        <Articles>
          {articlesCount !== null && articlesCount}
        </Articles>

        <Action>
          {type !== HelpCenterTreeModelType.ADD_ARTICLE && <Popover
            active={popoverActive}
            activator={
              <ActionButton onClick={onPopoverClick}>
                <svg viewBox='0 0 20 20' focusable='false' aria-hidden='true'><path d='M6 10a2 2 0 1 1-4.001-.001A2 2 0 0 1 6 10zm6 0a2 2 0 1 1-4.001-.001A2 2 0 0 1 12 10zm6 0a2 2 0 1 1-4.001-.001A2 2 0 0 1 18 10z' fillRule='evenodd'></path></svg>
              </ActionButton>
            }
            onClose={onPopoverClose}
            placement='bottom-end'
            strategy='fixed'
          >
            <ActionList
              actions={[
                { key: 'add-article', icon: 'book', content: t('Item::Add article'), visible: type === HelpCenterTreeModelType.COLLECTION, onClick: onItemAddArticleClick },
                { key: 'add-collection', icon: 'folder', content: t('Item::Add collection'), visible: node.data.type === HelpCenterTreeModelType.COLLECTION && node.level < 2, onClick: onItemAddCollectionClick },
                { key: 'edit', icon: 'edit-solid', content: t('Item::Edit'), onClick: onItemEditClick },
                { key: 'delete', icon: 'trash-alt-solid', content: t('Item::Delete'), destructive: true, onClick: onItemDeleteClick }
              ]}
              onClick={(_key: string) => {
                onPopoverClose()
              }}
              hasIndicator
            />
          </Popover>}
        </Action>
      </InfoContainer>
    </Container>
  )
}

export default Item